import { useMemo, type FC } from "react";
import { useTranslation } from "react-i18next";
import WizardStepper from "src/components/reusable/WizardStepper/WizardStepper";
import CampaignPageLayout from "src/layouts/Engagements/Campaigns/shared/CampaignPageLayout";
import CampaignFlowActions from "src/layouts/Engagements/Campaigns/shared/CampaignFlowActions";
import CampaignNextButton from "src/layouts/Engagements/Campaigns/shared/CampaignNextButton";
import { ShowFieldErrorsContext } from "src/layouts/Engagements/Campaigns/shared/UseRequiredFieldError";
import { CREATE_LIST_STEPS } from "./CreateList/createList.constants";
import { useCreateList } from "./CreateList/useCreateList";
import { useCreateListLabel } from "./CreateList/useCreateListLabel";
import UploadFileStep from "./CreateList/steps/UploadFileStep";
import MatchColumnsStep from "./CreateList/steps/MatchColumnsStep";
import NameTagsStep from "./CreateList/steps/NameTagsStep";
import ReviewStep from "./CreateList/steps/ReviewStep";
import ListReadyScreen from "./CreateList/ListReadyScreen";
import "./CreateList/CreateList.scss";

const CreateListScreen: FC = () => {
  const { t } = useTranslation();
  const label = useCreateListLabel();
  const wizard = useCreateList();
  const { stepIndex, stepKey, csv, cleanup, tags } = wizard;

  const stepperSteps = useMemo(
    () => CREATE_LIST_STEPS.map(step => ({ key: step.key, title: t(step.titleKey) })),
    [t]
  );

  const listMeta = useMemo(() => {
    const type = t("audienceOverview.typeUploaded");
    return tags.length ? `${type} · ${label("tagsSummary", { tags: tags.join(", ") })}` : type;
  }, [t, label, tags]);

  if (wizard.isCreated) {
    return (
      <CampaignPageLayout
        className="create-list"
        title={label("ready.pageTitle")}
        subtitle={label("ready.pageSubtitle")}
      >
        <ListReadyScreen
          listName={wizard.listName.trim()}
          listMeta={listMeta}
          peopleCount={wizard.peopleCount}
          phoneCount={wizard.phoneCount}
        />
      </CampaignPageLayout>
    );
  }

  const isBusy = wizard.parsing || wizard.checkingName || wizard.previewing || wizard.creating;
  const nextLabel = stepKey === "review" ? label("actions.createList") : label("actions.next");

  return (
    <CampaignPageLayout
      className="create-list"
      title={label("pageTitle")}
      subtitle={label("pageSubtitle")}
      stepper={<WizardStepper steps={stepperSteps} currentIndex={stepIndex} />}
    >
      <ShowFieldErrorsContext.Provider value={wizard.showFieldErrors}>
        {stepKey === "upload" && (
          <UploadFileStep
            csv={csv}
            parsing={wizard.parsing}
            onSelect={wizard.selectFile}
            onRemove={wizard.removeFile}
          />
        )}
        {stepKey === "match" && csv && (
          <MatchColumnsStep
            csv={csv}
            mapping={wizard.mapping}
            cleanup={cleanup}
            peopleCount={wizard.peopleCount}
            showErrors={wizard.showFieldErrors}
            onChange={wizard.setFieldMapping}
          />
        )}
        {stepKey === "details" && (
          <NameTagsStep
            listName={wizard.listName}
            description={wizard.description}
            tags={tags}
            availableTags={wizard.availableTags}
            onListNameChange={wizard.setListName}
            onDescriptionChange={wizard.setDescription}
            onAddTag={wizard.addTag}
            onRemoveTag={wizard.removeTag}
          />
        )}
        {stepKey === "review" && cleanup && wizard.preview && (
          <ReviewStep
            listName={wizard.listName.trim()}
            listMeta={listMeta}
            preview={wizard.preview}
            cleanup={cleanup}
            onDownloadSkipped={wizard.downloadSkippedRows}
            onDownloadList={wizard.downloadList}
          />
        )}
      </ShowFieldErrorsContext.Provider>

      <CampaignFlowActions onBack={wizard.goBack} backDisabled={isBusy}>
        <CampaignNextButton
          canProceed={wizard.canProceed}
          onNext={wizard.goNext}
          onBlockedClick={wizard.handleBlockedNext}
        >
          {wizard.creating ? label("actions.creating") : nextLabel}
        </CampaignNextButton>
      </CampaignFlowActions>
    </CampaignPageLayout>
  );
};

export default CreateListScreen;
